/**
 * Simple Angular controller that is attached to the GridTags within the index.aspx. The purpose of this is to allow the user to easily
 * update the after and before dates associated with tags.
 */
/* @ngInject */ export function bulkTagsController(
  $scope,
  Alert,
  BooksSvc,
  BusyScreen,
  SERVER,
  uiGridConstants
) {
  const TT_INDICATOR = SERVER.BooksDBConstants.TT_INDICATOR;
  const TT_MEMBERSHIP = SERVER.BooksDBConstants.TT_MEMBERSHIP;

  const alertTitle = 'Tag Dates';

  const COLUMNS = [
    { field: 'indicator', width: 100 },
    { field: 'description', cellTooltip: true },
    {
      field: 'assignedAfterDate',
      displayName: 'After',
      cellFilter: 'date:"M/d/yyyy"',
      width: 100
    },
    {
      field: 'assignedBeforeDate',
      displayName: 'Before',
      cellFilter: 'date:"M/d/yyyy"',
      width: 100
    }
  ];

  const gridOptions = {
    data: 'dbTags',
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableFiltering: true,
    enableRowSelection: true,
    enableSelectAll: true,
    minRowsToShow: 3,
    multiSelect: true,
    enableFullRowSelection: true,
    enableColumnResizing: true,
    showGridFooter: true,
    /**
     * We register the grid api to the $scope so we have reference to which rows are clicked and that way we can interact with them in this file.
     */
    onRegisterApi: function (gridApi) {
      $scope.gridApi = gridApi;

      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifySingleSelect(selectedRows.length, row, alertTitle, 1000);
      });
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifyMultiSelect(selectedRows.length, rows, alertTitle, 1000);
      });
    },
    columnDefs: COLUMNS
  };

  /**
   * Our grid options for the Tags are named tagGridOptions, so in order for this to interact with the grid we need to set that within
   * the $scope.
   */
  $scope.tagGridOptions = gridOptions;

  /**
   * Grabs the list of selected rows and extracts the tags ids from them. This is used to pass to the server to update the tags.
   * We update the tags dates by passing the tags ids and the after and before dates to the server. The server then updates the tags
   * with the new dates.
   *
   * @returns void
   */
  function updateTagsDates() {
    const selectedRows = $scope.gridApi.selection.getSelectedRows();

    if (!selectedRows.length) {
      Alert.info(
        'No tags have been selected. Please select one or more before continuing.',
        alertTitle
      );
      return;
    }

    /**
     * Filter out all the tags that already have the same after and before dates. This is to prevent extra updates that are not needed.
     */
    const tagsToChange = selectedRows
      .filter((tag) => {
        /**
         * Converted to date string to avoid time zone issues. This will only compare the date and not the time.
         */
        const afterDatesAreNotEqual =
          tag.assignedAfterDate &&
          tag.assignedAfterDate.toDateString() !==
            $scope.props.assignedAfter.toDateString();
        const beforeDatesAreNotEqual =
          tag.assignedBeforeDate &&
          tag.assignedBeforeDate.toDateString() !==
            $scope.props.assignedBefore.toDateString();

        return afterDatesAreNotEqual || beforeDatesAreNotEqual;
      })
      .map((tag) => tag.id);

    if (!tagsToChange.length) {
      Alert.info(
        'The tag(s) you have selected already have those dates assigned.',
        alertTitle
      );
      return;
    }

    BusyScreen.setBusy(true);

    BooksSvc.bulkChangeTagDates(
      tagsToChange,
      $scope.props.assignedAfter.toDateString(),
      $scope.props.assignedBefore.toDateString()
    )
      .then(() => BooksSvc.getTagList([TT_INDICATOR, TT_MEMBERSHIP].join(',')))
      .then((resp) => {
        // Refresh the tags data now that we have updated the tags
        $scope.dbTags = resp.data.membershipTags
          .concat(resp.data.indicatorTags)
          .sortOn('display');
        // Reset the after and before dates so the user can change them again.
        $scope.props.assignedAfter = null;
        $scope.props.assignedBefore = null;

        BusyScreen.setBusy(false);

        Alert.success(
          'The selected tag(s) have had their dates updated.',
          alertTitle
        );
      })
      .catch((error) => {
        BusyScreen.setBusy(false);

        const errorMesssage =
          error.parsedResponse && error.parsedResponse.message()
            ? error.parsedResponse.message()
            : 'There was an error updating the tag(s) dates. Please try again.';

        Alert.error(errorMesssage, alertTitle);
      });
  }

  /**
   * Simple function that checks to see if there are any selected rows. If there are then we can save, if not then we cannot save.
   * If there are no selected rows then the user has not selected anything to save. This also checks to see if the user has selected
   * an after and before date. If they have not then we cannot save.
   *
   * @returns boolean (true = can Save; false = cannot save)
   */
  function canSaveTags() {
    return (
      $scope.gridApi.selection.getSelectedRows().length > 0 &&
      $scope.props.assignedAfter &&
      $scope.props.assignedBefore
    );
  }

  /**
   * Attach these to the $scope so they can be used in the index file.
   */
  $scope.updateTagsDates = updateTagsDates;
  $scope.canSaveTags = canSaveTags;
}
